// Upload / remove your profile picture (resized client-side to 400px JPEG).
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import SquadAvatar from "./SquadAvatar";

function resize(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const side = Math.min(img.width, img.height);
      const c = document.createElement("canvas");
      c.width = c.height = 400;
      c.getContext("2d").drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, 400, 400);
      c.toBlob((b) => (b ? resolve(b) : reject(new Error("resize failed"))), "image/jpeg", 0.88);
    };
    img.onerror = () => reject(new Error("Not a valid image"));
    img.src = URL.createObjectURL(file);
  });
}

export default function AvatarUpload({ size = 72, status, onChanged, compact = false }) {
  const { user, refresh } = useAuth();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) { toast.error("Please choose a JPG, PNG or WebP image"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", await resize(f), "avatar.jpg");
      await api.post("/squad/avatar", fd);
      await refresh(); onChanged?.(); toast.success("Profile picture updated");
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Upload failed"); }
    finally { setBusy(false); }
  };
  const remove = async () => { await api.delete("/squad/avatar"); await refresh(); onChanged?.(); toast.success("Profile picture removed"); };
  return (
    <div className="flex items-center gap-3">
      <button type="button" onClick={() => ref.current?.click()} className="relative group rounded-full" data-testid="avatar-upload-btn" title="Change profile picture">
        <SquadAvatar name={user?.name} url={user?.avatar_url} status={status} size={size} />
        <span className="absolute inset-0 rounded-full bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
          {busy ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Camera className="w-4 h-4 text-white" />}
        </span>
      </button>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="avatar-upload-input" />
      {!compact && user?.avatar_url && (
        <button type="button" onClick={remove} className="text-[11px] text-slate-400 hover:text-rose-300 inline-flex items-center gap-1" data-testid="avatar-remove-btn">
          <Trash2 className="w-3 h-3" /> Remove
        </button>
      )}
    </div>
  );
}
